import _ from "lodash";
import { transformToFormattedValue } from "../formServiceFunctions";
import { buildCalendlyLink, buildCalendlyLinkLawyer } from "../calendlyService";
import { getAdvoAssistRepresentative, getCaseLink, getServiceWorldCaseLink, getServiceWorldCaseMessagingLink } from "../Product/ProductService/productService";
import { listAddress, listAllClients, listPersonWithAddress } from "../formatterService";
import { Person } from "../../types/Person";
import { Address } from "../../types/Address";
import { FieldValues } from "react-hook-form";
import { ProductUnion } from "../../types/ProductUnion";
import { AbstractCase } from "../../types/AbstractCase";

const SPECIAL_PATHS_WITH_VALUE = ["customer.contactTime"];

// The template editor previously saved traffic accident templates with the generic litigation recipient.
export const normalizeTemplateRecipientType = (productClassName: string, recipientType: string) =>
  productClassName === "trafficAccident" && recipientType === "opposingLiabilityInsurance"
    ? "opposingCarLiabilityInsurance"
    : recipientType;

export const matchesTemplateRecipientType = (
  selectedRecipientType: string,
  templateProductClassName: string,
  templateRecipientType: string
) =>
  selectedRecipientType.startsWith(templateRecipientType) ||
  selectedRecipientType.startsWith(normalizeTemplateRecipientType(templateProductClassName, templateRecipientType));

export const fillTemplate = (templateString: string, productEntity: ProductUnion, formValues: FieldValues) => {

  /*
  transform parameters into readable keys to check if they are present in the productEntity
  because the whole thing crashes if the method tries to replace a placeholder which doesnt exist,
  for example if someone puts <%= wurst.broetchen %> as a placeholder and there is no wurst object

  const parameterMatches = templateString.match(/<%=.+%>/g);
  const parameters = _.map(parameterMatches, match => {
    let isolatedParameter = _.trimStart(match, "<%=");
    isolatedParameter = _.trimEnd(isolatedParameter, "%>");
    return _.trim(isolatedParameter);
  });

  check if all keys exist in productEntity with _.every or something like that
  */

  const compiled = _.template(templateString);
  try {
    return compiled({
      [productEntity.productClassName]: productEntity,
      product: productEntity,
      formValues: formValues,
      translate,
      salutation,
      greeting,
      getCaseLink,
      getServiceWorldCaseLink,
      getServiceWorldCaseMessagingLink,
      wrapInLinkTag,
      getWeRepresentAllClients
    });
  } catch (e) {
    console.error(e);
    return "Fehler in der Vorlage. Dies liegt höchstwahrscheinlich an einem falschen Platzhalter.";
  }
};

export const salutation = (product: ProductUnion, formValues: FieldValues) => {
  if (!formValues?.recipientType) {
    return getPersonalSalutation(product.client);
  }

  if (_.startsWith(formValues.recipientType, "processParticipantsPeople")) {
    const recipientId = formValues.recipientType.split(".")[1];
    const recipient = product.processParticipantsPeople.find((person: Person) => String(person.id) === recipientId);

    return getPersonalSalutation(recipient, false, true);
  }

  if (_.startsWith(formValues.recipientType, "processParticipantsOrganizations")) {
    return "Sehr geehrte Damen und Herren,";
  }

  switch (formValues.recipientType) {
    case "contractualPartnerLegalEntity":
      return product.opposingPartyType === "opposingPartyPerson"
        ? getPersonalSalutation(product.contractualPartnerPerson)
        : "Sehr geehrte Damen und Herren,";
    case "opponentLawFirm":
      return getLawyerSalutation(product.opponentLawyer);
    case "authority":
    case "prosecution":
    case "court":
      return "";
    case "customer":
    case "petitioner":
    case "client":
      return getAllClientsSalutation(product);
    case "advoAssistRepresentative":
      return getLawyerSalutation(getAdvoAssistRepresentative(product));
    case "debtCollectionAgency":
    case "insurance":
    case "custom":
    default:
      return "Sehr geehrte Damen und Herren,";
  }
};

export const greeting = (product: ProductUnion, formValues: FieldValues) => {
  if (formValues.recipientType === "opponentLawFirm") {
    return "<p>Mit kollegialen Grüßen</p>";
  }

  return "<p>Mit freundlichen Grüßen</p>";
};

export const getWeRepresentAllClients = (product: ProductUnion) => {

  let initialPart;

  if (product.clientWasMinorAtContractDate) {
    initialPart = `in vorbezeichneter Angelegenheit zeigen wir an, dass uns unsere minderjährige Mandantschaft ` +
      `${listPersonWithAddress(product.client, true)}, ` +
      `vertreten durch ${(product.clientLegalRepresentativeAmount === 2 && product.clientLegalRepresentativesAuthorizationType === "both") ? "ihre" : "ihren"}`
      + ` gesetzlichen Vertreter ${listPersonWithAddress(product.clientLegalRepresentative)} ` +
      `${(product.clientLegalRepresentativeAmount === 2 && product.clientLegalRepresentativesAuthorizationType === "both") ?
        `sowie ${listPersonWithAddress(product.secondClientLegalRepresentative)}`
        : ""}` +
      ` mit der Wahrnehmung und Vertretung ihrer rechtlichen Interessen beauftragt hat.`;

  } else {
    initialPart = `in vorbezeichneter Angelegenheit zeigen wir an, dass uns unsere Mandantschaft ${listAllClients(product)},` +
      ` wohnhaft ${listAddress(product.client.residenceAddress)}, zur Wahrnehmung und Vertretung ihrer rechtlichen Interessen beauftragt hat.`;
  }

  return initialPart + " " + (product.powerOfAttorneyDocument ?
    (`Eine auf uns lautende Vollmacht ${product.clientWasMinorAtContractDate ? "des gesetzlichen Vertreters " : ""}haben wir diesem Schreiben beigefügt.`) :
    "Unsere ordnungsgemäße Bevollmächtigung wird anwaltlich versichert.");
};
const getPersonalSalutation = (
  person: Person | AbstractCase["client"] | undefined,
  firstLetterLowercase = false,
  includeGivenName = false
) => {
  if (!person) {
    return "";
  }
  const name = includeGivenName ? [person.givenName, person.familyName].filter(Boolean).join(" ") : person.familyName;

  return `${firstLetterLowercase ? "sehr" : "Sehr"} ${person.gender === "female" ? "geehrte Frau" : "geehrter Herr"} ${
    name
  },`;
};
const getAllClientsSalutation = (product: ProductUnion) => {
  if (product.additionalClients.length !== 1) {
    return getPersonalSalutation(product.client);
  }
  return `${getPersonalSalutation(product.client)} ${getPersonalSalutation(product.additionalClients[0], true)}`;
};
const getLawyerSalutation = (person?: Person) => {
  if (!person) {
    return "";
  }
  const salutation = person.gender === "female" ? "Sehr geehrte Frau Kollegin" : "Sehr geehrter Herr Kollege";
  return salutation + " " + person.familyName + ",";
};

const generateCalendlyLinkLawyer = (product: ProductUnion) => {
  return wrapInLinkTag(buildCalendlyLinkLawyer(product), "Termin vereinbaren");
};

const generateCalendlyLinkRoundRobin = (product: ProductUnion) => {
  return wrapInLinkTag(buildCalendlyLink(product), "Termin vereinbaren");
};

const wrapInLinkTag = (url: string, label: string = "Hier klicken") => {
  return "<a href=" + url + "><u><strong>" + _.toUpper(label) + "</strong></u></a>";
};

const getRubrumLitigationCase = (product: ProductUnion) => {
  switch (product.caseCategory) {
    case "defendClaim":
      return product.opponentVsClientString;
    default:
      return product.clientVsOpponentString;
  }
};

export const getGenderedResponsibleLawyer = (product: ProductUnion, showSalutation = false) => {
  if (!product.backofficeCase.lawyer?.person?.gender) {
    return "";
  }

  const genderedSalutation = product.backofficeCase.lawyer.person.gender === "male" ? "Herrn " : "Frau ";
  const salutation = showSalutation ? genderedSalutation : "";

  const title = product.backofficeCase.lawyer.person.gender === "male"
    ? "Rechtsanwalt " : "Rechtsanwältin ";

  return salutation + title + product.backofficeCase.lawyer.person.fullname;
};

const getAddressWithPostOfficeBoxFallback = (address: Address) => {
  if (address.streetAddress && address.postalCode && address.addressLocality) {
    return `${address.streetAddress}, ${address.postalCode} ${address.addressLocality}`;
  }
  if (address.postOfficeBoxNumber && address.postOfficeBoxPostalCode && address.postOfficeBoxAddressLocality) {
    return `Postfach: ${address.postOfficeBoxNumber}, ${address.postOfficeBoxPostalCode} ${address.postOfficeBoxAddressLocality}`;
  }
  return "Adressdaten nicht vollständig";
};

const getFileReferenceRecipient = (product: ProductUnion, formValues: FieldValues) => {
  switch (formValues.recipientType) {
    case "court":
      return product.courtReference;
    case "contractualPartnerLegalEntity":
    case "contractualPartner":
      return product.contractNumber;
    case "debtCollectionAgency":
      return product.debtCollectionAgencyReference;
    case "opponentLawFirm":
      return product.referenceOpponentLawyer;
    case "insurance":
      return product.caseInsurance.caseReferenceNumber;
    default:
      return null;
  }
}

const handleSpecialPaths = (product: ProductUnion, path: string, formValues?: FieldValues) => {
  switch (path) {
    case "rubrumLitigationCase":
      return getRubrumLitigationCase(product);
    case "rubrumTrafficAccident":
      return getRubrumLitigationCase(product) + " u.a.";
    case "genderedResponsibleLawyer":
      return getGenderedResponsibleLawyer(product);
    case "calendlyLink":
    case "calendlyLinkRoundRobin":
      return generateCalendlyLinkRoundRobin(product);
    case "calendlyLinkLawyer":
      return generateCalendlyLinkLawyer(product);
    case "appealRevokeLink":
      return wrapInLinkTag(
        getServiceWorldCaseLink(product) +
        "/einspruch-ruecknahme?&utm_source=legalbird&utm_medium=email&utm_campaign=feedback",
        "Jetzt Rückmeldung geben"
      );
    case "furtherProcessLink":
      return wrapInLinkTag(
        getServiceWorldCaseLink(product) + "/rueckmeldung?&utm_source=legalbird&utm_medium=email&utm_campaign=feedback",
        "Jetzt weiteres Vorgehen festlegen"
      );
    case "customer.contactTime":
      const value = _.get(product, path);
      return value === "flexible" ? "Ich bin flexibel" : "gegen " + value + " Uhr";
    case "weRepresentAllClients":
      return getWeRepresentAllClients(product);
    case "referenceRecipient":
      return getFileReferenceRecipient(product, formValues ?? {});
    default:
      break;
  }

  if (path.startsWith("addressWithPostOfficeBoxFallback.")) {
    return getAddressWithPostOfficeBoxFallback(_.get(product, path.replace("addressWithPostOfficeBoxFallback.", "")));
  }

  return "";
};

const translate = (product: ProductUnion, path: string, formValues?: FieldValues) => {
  let value = _.get(product, path);
  if (value === undefined || value === null || _.includes(SPECIAL_PATHS_WITH_VALUE, path)) {
    return handleSpecialPaths(product, path, formValues);
  }
  return transformToFormattedValue(product, path);
};
